import { rotaDaTransicao } from '@/features/acoes/rotulos';
import { requisitar } from '@/lib/http';
import type {
  AcaoDetalhada,
  AreaSesmt,
  ComplexidadeAcao,
  EstadosDeWorkItem,
  ModuloDoSistema,
  PaginaDeAcoes,
  PrioridadeAcao,
  ResumoDeAcoes,
  SaudePrazo,
  SituacaoAcao,
  TipoArea,
  TipoTransicao,
} from '@/types/api';

/**
 * Endpoints de acoes. Especificacao V3.1, item 6.
 *
 * O ESCOPO DE AREA NAO APARECE AQUI, de proposito. Ele vem do token, no servidor,
 * e nao e negociavel pelo cliente. Um parametro de area nesta camada sugeriria que
 * o cliente escolhe o que ve.
 */

const BASE = '/api/acoes';

/*
  O teto de 200 e o padrao de 50 sao do servidor, e ninguem no cliente precisava dos dois
  numeros: a tela nao escolhe tamanho de pagina. Estavam declarados aqui como constantes
  exportadas que nada lia — copia de um contrato que o proprio servidor aplica.
*/

export interface FiltroDeAcoes {
  situacoes?: SituacaoAcao[];
  /**
   * Naturezas de area responsavel. Recorte de painel, nao filtro do usuario.
   *
   * O Painel Produto manda `['Produto']` para a fila nao vir com as acoes de COE, FAP
   * Clinicas, Rede Credenciada e Suporte. O Painel CS nao manda nada, porque acompanha o
   * que foi solicitado, qualquer que seja a area que executa.
   */
  tiposDeAreaResponsavel?: TipoArea[];
  /**
   * Clientes a incluir. Multi-selecao, mesmo padrao de `situacoes`: uma acao que afeta
   * qualquer um dos clientes selecionados entra no resultado.
   */
  clienteIds?: number[];
  /**
   * Saudes do prazo a incluir. Multi-selecao, mesmo padrao de `situacoes`.
   *
   * NAO E COLUNA: o servidor calcula a saude a partir de prazo, situacao e a data de
   * hoje, a cada consulta — o mesmo calculo que preenche `saudeDoPrazo` na resposta.
   */
  saudesDoPrazo?: SaudePrazo[];
  /**
   * Somente acoes cuja task no Azure esta atribuida a quem esta olhando.
   *
   * O e-mail NAO vai daqui: o servidor o le do token. Mandar e-mail do cliente permitiria
   * ver a fila de qualquer pessoa.
   */
  apenasMinhasTasks?: boolean;
  texto?: string;
  apenasImpedidas?: boolean;
  apenasComComplementacaoPendente?: boolean;
  pagina?: number;
  tamanhoDaPagina?: number;
}

/**
 * Monta a query string.
 *
 * Omite o que nao foi informado, em vez de enviar vazio. `texto=` e
 * `apenasImpedidas=false` nao significam a mesma coisa que ausencia para o
 * servidor, e enviar tudo sempre tornaria a chave de cache do TanStack Query
 * diferente a cada digitacao.
 */
function montarConsulta(filtro: FiltroDeAcoes): string {
  const parametros = new URLSearchParams();

  // O servidor aceita `situacao` repetido, nao uma lista separada por virgula.
  for (const situacao of filtro.situacoes ?? []) {
    parametros.append('situacao', situacao);
  }

  // Mesma forma repetida do `situacao`, e nao lista separada por virgula.
  for (const tipo of filtro.tiposDeAreaResponsavel ?? []) {
    parametros.append('tipoAreaResponsavel', tipo);
  }

  for (const clienteId of filtro.clienteIds ?? []) {
    parametros.append('clienteId', String(clienteId));
  }

  for (const saude of filtro.saudesDoPrazo ?? []) {
    parametros.append('saudeDoPrazo', saude);
  }

  const texto = filtro.texto?.trim();

  if (texto !== undefined && texto !== '') {
    parametros.set('texto', texto);
  }

  if (filtro.apenasImpedidas === true) {
    parametros.set('apenasImpedidas', 'true');
  }

  if (filtro.apenasComComplementacaoPendente === true) {
    parametros.set('apenasComComplementacaoPendente', 'true');
  }

  if (filtro.apenasMinhasTasks === true) {
    parametros.set('apenasMinhasTasks', 'true');
  }

  if (filtro.pagina !== undefined && filtro.pagina > 1) {
    parametros.set('pagina', String(filtro.pagina));
  }

  if (filtro.tamanhoDaPagina !== undefined) {
    parametros.set('tamanhoDaPagina', String(filtro.tamanhoDaPagina));
  }

  const consulta = parametros.toString();

  return consulta === '' ? '' : `?${consulta}`;
}

/** Lista acoes dentro do escopo de area do usuario. */
export function listarAcoes(filtro: FiltroDeAcoes, sinal?: AbortSignal): Promise<PaginaDeAcoes> {
  return requisitar<PaginaDeAcoes>(`${BASE}${montarConsulta(filtro)}`, {
    ...(sinal === undefined ? {} : { sinal }),
  });
}

/**
 * Contagens do painel.
 *
 * NAO RECEBE O FILTRO DA TABELA. O painel mostra o retrato do painel inteiro, e e ele que
 * serve de ponto de partida para filtrar. Se respondesse ao filtro, clicar num tile de 12
 * itens faria os outros tiles zerarem, e nao haveria como voltar.
 *
 * RECEBE O RECORTE DE PAINEL, que e outra coisa: e a fronteira do painel, nao uma escolha
 * do usuario. Se os tiles ignorassem o recorte, o Painel Produto somaria as acoes do COE
 * nos numeros de cima e nao as mostraria na tabela.
 *
 * RECEBE OS CLIENTES FILTRADOS: com cliente marcado, situacao, prazo e area contam so as
 * acoes dele. A lista por cliente segue completa, porque e o seletor.
 */
export function obterResumo(
  tiposDeAreaResponsavel: TipoArea[],
  clienteIds: number[],
  sinal?: AbortSignal,
): Promise<ResumoDeAcoes> {
  const parametros = new URLSearchParams();

  for (const tipo of tiposDeAreaResponsavel) {
    parametros.append('tipoAreaResponsavel', tipo);
  }

  for (const clienteId of clienteIds) {
    parametros.append('clienteId', String(clienteId));
  }

  const consulta = parametros.toString();

  return requisitar<ResumoDeAcoes>(`${BASE}/resumo${consulta === '' ? '' : `?${consulta}`}`, {
    ...(sinal === undefined ? {} : { sinal }),
  });
}

/**
 * Estado dos work items no Azure DevOps.
 *
 * CHAMADA SEPARADA DA LISTAGEM, de proposito. Embutir o status na resposta de `/api/acoes`
 * faria a tabela esperar o Azure e falhar junto com ele: 242 acoes deixariam de carregar
 * porque um servico de terceiro esta lento. Aqui a lista chega primeiro e a coluna preenche
 * depois.
 *
 * Nunca rejeita por indisponibilidade do Azure: o corpo carrega `disponivel: false` e o
 * motivo. Um 503 faria o interceptor de HTTP tratar como erro de sessao.
 */
export function obterEstadosDeWorkItem(
  ids: number[],
  sinal?: AbortSignal,
): Promise<EstadosDeWorkItem> {
  const parametros = new URLSearchParams();

  for (const id of ids) {
    parametros.append('workItem', String(id));
  }

  return requisitar<EstadosDeWorkItem>(`${BASE}/work-items?${parametros.toString()}`, {
    ...(sinal === undefined ? {} : { sinal }),
  });
}

/**
 * Vincula, troca ou desvincula a task do Azure DevOps.
 *
 * Manda o NUMERO, nunca a URL: o endereco e derivado de configuracao no servidor.
 * `null` desvincula.
 *
 * A rota pertence ao grupo Processo, entao exige `acao.editarProcesso` e so e aceita nas
 * situacoes em que aquele grupo esta aberto. Quem decide isso e o dominio; a tela apenas
 * nao oferece o campo quando `gruposEditaveis` nao traz Processo.
 */
export function definirWorkItem(
  id: number,
  workItem: number | null,
  motivo?: string,
): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/work-item`, {
    metodo: 'PUT',
    corpo: { workItem, motivo: motivo ?? null },
  });
}

/** Dados para criar uma acao. */
export interface NovaAcao {
  assunto: string;
  descricao: string | null;
  planoAcao: string | null;
  areaResponsavelId: number;
  areaSolicitanteId: number | null;
  /** Opcional. */
  areaSesmt: AreaSesmt | null;
  /** Obrigatorio. */
  modulo: ModuloDoSistema;
  /** Ao menos um. Decisao 11, garantida pelo dominio e por CHECK no banco. */
  clienteIds: number[];
  /**
   * Nasce como rascunho.
   *
   * O padrao do servidor tambem e `true`, e a tela nao oferece a alternativa: enviar
   * direto para analise sem revisar e o tipo de atalho que produz solicitacao
   * incompleta na fila do Produto.
   */
  comoRascunho: boolean;
}

/** Cria uma acao. Responde 201 com a acao detalhada. */
export function criarAcao(nova: NovaAcao): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(BASE, { metodo: 'POST', corpo: nova });
}

/** Conteudo de origem editavel. Mesmos campos de `EdicaoDeOrigem`, no servidor. */
export interface EdicaoDeOrigem {
  assunto: string;
  descricao: string | null;
  planoAcao: string | null;
  areaSolicitanteId: number | null;
  /** Opcional. */
  areaSesmt: AreaSesmt | null;
  /** Obrigatorio. */
  modulo: ModuloDoSistema;
}

/**
 * Edita o conteudo de origem: assunto, descricao, plano de acao e area solicitante.
 *
 * A rota pertence ao grupo Origem, entao exige `acao.editarOrigem` e so e aceita nas
 * situacoes em que aquele grupo esta aberto — Rascunho e AjusteSolicitado. Quem decide
 * isso e o dominio; a tela apenas nao oferece o formulario quando `gruposEditaveis` nao
 * traz Origem.
 */
export function editarOrigem(id: number, edicao: EdicaoDeOrigem): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/origem`, {
    metodo: 'PUT',
    corpo: edicao,
  });
}

/**
 * Substitui a lista de clientes afetados.
 *
 * Mesma permissao e mesmo grupo de `editarOrigem`. Rota separada porque o dominio trata
 * clientes como sua propria operacao (AlterarClientesAsync), com a regra de "ao menos um
 * cliente" validada ali.
 */
export function alterarClientes(id: number, clienteIds: number[]): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/clientes`, {
    metodo: 'PUT',
    corpo: { clienteIds },
  });
}

/**
 * Define ou remove o responsavel pela execucao.
 *
 * Grupo Processo: aceita escrita de Aguardando analise em diante, fechado no rascunho e
 * nas terminais. A tela nao oferece o campo quando `gruposEditaveis` nao traz Processo.
 */
export function definirResponsavel(
  id: number,
  responsavelId: number | null,
  motivo?: string,
): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/responsavel`, {
    metodo: 'PUT',
    corpo: { responsavelId, motivo: motivo ?? null },
  });
}

/** Define ou remove a prioridade. Mesmo grupo e mesma janela de Responsavel/Prazo. */
export function definirPrioridade(
  id: number,
  prioridade: PrioridadeAcao | null,
): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/prioridade`, {
    metodo: 'PUT',
    corpo: { prioridade },
  });
}

/**
 * Define ou repactua o prazo.
 *
 * MOTIVO OBRIGATORIO AO REPACTUAR, e nao so no primeiro registro: o dominio recusa com
 * 400 trocar um prazo ja existente sem dizer por que. A tela decide quando exigir o
 * campo olhando se `acao.prazo` ja e diferente de `null`, nao reimplementando a regra.
 */
export function definirPrazo(
  id: number,
  prazo: string | null,
  motivo?: string,
): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/prazo`, {
    metodo: 'PUT',
    corpo: { prazo, motivo: motivo ?? null },
  });
}

/**
 * Corrige o Assunto fora do grupo Origem.
 *
 * EXCECAO PONTUAL A DECISAO 16 ("o Produto nunca sobrescreve o conteudo do CS"), e nao a
 * revogacao dela. `editarOrigem` continua sendo o caminho normal do CS, fechado fora de
 * Rascunho/AjusteSolicitado; esta rota abre quando o grupo Processo esta aberto — ou
 * seja, quando o Produto ja responde pela acao — e e so para o Assunto.
 *
 * Motivo obrigatorio: desfazer o titulo que o cliente pediu so se sustenta com o
 * registro de por que foi feito. A validacao de obrigatoriedade e do servidor; aqui o
 * parametro e opcional so para o formato da chamada bater com os outros `definir*`.
 */
export function corrigirAssunto(
  id: number,
  assunto: string,
  motivo: string,
): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/corrigir-assunto`, {
    metodo: 'PUT',
    corpo: { assunto, motivo },
  });
}

/** PO, Dev, Qa, Complexidade, Financiada e Encaixe. Mesmos campos de `DadosDeProduto`. */
export interface DadosDeProduto {
  po: string | null;
  dev: string | null;
  qa: string | null;
  complexidade: ComplexidadeAcao | null;
  financiada: boolean;
  encaixe: boolean;
}

/**
 * Define PO, Dev, Qa, Complexidade, Financiada e Encaixe.
 *
 * EXCLUSIVO DE AREA RESPONSAVEL DO TIPO PRODUTO. O servidor recusa com 409 quando a area
 * nao e Produto, mas essa checagem roda em `ServicoDeAcoes`, nao na matriz de grupos — a
 * tela decide se mostra o formulario olhando `acao.tipoAreaResponsavel`, nao
 * `gruposEditaveis` (que continua sendo so sobre o grupo Processo como um todo).
 */
export function definirDadosDeProduto(id: number, dados: DadosDeProduto): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/dados-de-produto`, {
    metodo: 'PUT',
    corpo: dados,
  });
}

/** Obtem uma acao. Responde 404 tambem quando existe fora do escopo do usuario. */
export function obterAcao(id: number, sinal?: AbortSignal): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}`, {
    ...(sinal === undefined ? {} : { sinal }),
  });
}

/**
 * Aplica uma transicao.
 *
 * A rota e derivada do tipo, e nao recebida como texto: uma rota generica com o
 * tipo no corpo deslocaria a autorizacao do pipeline para dentro do metodo no
 * servidor, e e por isso que existem 12 rotas.
 */
export function transicionar(
  id: number,
  transicao: TipoTransicao,
  justificativa?: string,
): Promise<AcaoDetalhada> {
  return requisitar<AcaoDetalhada>(`${BASE}/${String(id)}/${rotaDaTransicao[transicao]}`, {
    metodo: 'POST',
    corpo: { justificativa: justificativa ?? null },
  });
}
